import React, { Component } from "react";
import { Table, Grid, Icon } from "semantic-ui-react";
import find from "lodash/find";

import CardShell from "./CardShell";
import {
  subjectFinalGrade,
  mapehComposite,
  semesterAverage,
  conductFinalGrade,
  generalAverage,
  passFailLabel,
} from "../cardMath";
import {
  resolveSubjectTemplate,
  categoryMatches,
  categoryInList,
} from "../subjectTemplate";
import { clubRows, clubMark } from "../clubMembership";

const _ = { find };

const GROUP_HEADER = { backgroundColor: "#E0E0E0", fontStyle: "italic" };

// The form greys out the terms a subject is not taken in. Kendo's PDF export
// renders a cell background reliably; a CSS class would not survive it.
const NOT_TAKEN = { backgroundColor: "#BFBFBF" };

const SUB_ROW = { paddingLeft: "30px", fontStyle: "italic" };

/**
 * Grade 11's report card under the Strengthened SHS curriculum (DM 012, s.
 * 2026), laid out to match the final DepEd form the registrar supplied.
 *
 * Grade 11 used to share CardNumeric3Term with Grades 2-10. It no longer can:
 * the form adds a Units column, groups rows under Core and Elective headings,
 * prints one learning area as a parent row over two sub-rows, greys out the
 * terms an elective is not taken in, and totals units rather than terms on its
 * General Average line. Five conditionals in a card that also has to keep
 * printing Grade 7 correctly is how Grade 7 breaks, so this is its own file.
 *
 * Grade 12 is not here either - it keeps the 2016 curriculum subjects on its
 * own whole-year table in CardSHS12.
 *
 * Two rules are worth knowing before reading the code:
 *
 *   - Effective Communication and Mabisang Komunikasyon are one 6-unit learning
 *     area taught as two subjects. The parent row averages them and carries the
 *     units; they print beneath it and count once, not twice.
 *   - An elective runs for a single term, so its final grade is that term's
 *     mark. Averaging it over three terms the way a core subject is averaged
 *     would divide a real grade by two empty ones - and, worse, the whole-year
 *     rule withholds a final until Term 3, which for a Term 1 elective never
 *     arrives, leaving the card INC for the rest of the year.
 */
class CardSHS11 extends Component {
  state = { showAverage: false };

  componentDidMount() {
    this.setState({ showAverage: this.props.sectionStudent.showFinalGradeAverage });
  }

  componentDidUpdate(prevProps) {
    if (
      JSON.stringify(prevProps.sectionStudent) !==
      JSON.stringify(this.props.sectionStudent)
    ) {
      this.setState({
        showAverage: this.props.sectionStudent.showFinalGradeAverage,
      });
    }
  }

  toggleAverage = () =>
    this.setState((prev) => ({ showAverage: !prev.showAverage }));

  canSeeAverage() {
    const { userType } = this.props;
    return (
      userType === "admin" || userType === "principal" || userType === "registrar"
    );
  }

  subjectFor(subjectCategoryValue) {
    return _.find(
      this.props.sectionSubjects,
      (o) => categoryMatches(subjectCategoryValue, o.subject.subjectCategory)
    );
  }

  gradeFor(sectionSubject) {
    if (!sectionSubject) return null;
    return _.find(
      this.props.summary.grades,
      (g) => g.sectionSubjectId === sectionSubject.sectionSubjectId
    );
  }

  render() {
    const { scheme, sectionStudent, teachers } = this.props;

    const section = sectionStudent.section;
    const periods = scheme.periods;

    const template = resolveSubjectTemplate(section, scheme);
    const coreSlots = template.Core || [];
    const electiveSlots = template.Electives || [];

    const finalGrades = [];
    // The template, not the section, says how many learning areas a Grade 11
    // card is meant to carry. A missing one leaves finalGrades short, which is
    // what generalAverage reads as INC.
    const expectedCount = coreSlots.length + electiveSlots.length;

    const coreRows = [];
    coreSlots.forEach((slot, i) => {
      if (slot.components) {
        const componentSubjects = slot.components.map((c) => this.subjectFor(c.value));
        const componentGrades = componentSubjects
          .map((s) => this.gradeFor(s))
          .filter(Boolean);

        // The area is the average of BOTH subjects. Handed only one, the
        // composite would average that one and report a full learning-area
        // grade sitting above an empty sub-row - and count the area as
        // complete, so the card would print a General Average that silently
        // left half of a 6-unit area out. It is scored once both are there.
        const composite =
          componentGrades.length === slot.components.length
            ? mapehComposite(componentGrades, periods)
            : { periodGrades: periods.map(() => 0), finalGrade: 0 };

        if (composite.finalGrade) finalGrades.push(composite.finalGrade);

        coreRows.push(
          this.renderRow({
            key: `core-${i}`,
            label: slot.text,
            units: slot.units,
            periodValues: composite.periodGrades,
            finalGrade: composite.finalGrade,
          })
        );

        // The sub-rows are the subjects actually graded. They carry no units
        // and no remarks of their own - the parent row is the learning area.
        slot.components.forEach((component, j) => {
          const grade = this.gradeFor(componentSubjects[j]);
          coreRows.push(
            this.renderRow({
              key: `core-${i}-${j}`,
              label: component.text,
              subRow: true,
              periodValues: periods.map((p) =>
                grade ? Number(grade[p.dbField]) || 0 : 0
              ),
              finalGrade: subjectFinalGrade(grade, periods),
            })
          );
        });
        return;
      }

      const grade = this.gradeFor(this.subjectFor(slot.value));
      const finalGrade = subjectFinalGrade(grade, periods);
      if (finalGrade) finalGrades.push(finalGrade);

      coreRows.push(
        this.renderRow({
          key: `core-${i}`,
          label: slot.text,
          units: slot.units,
          periodValues: periods.map((p) => (grade ? Number(grade[p.dbField]) || 0 : 0)),
          finalGrade,
        })
      );
    });

    const electiveRows = this.placeElectives(coreSlots, electiveSlots).map(
      (placement, i) => {
        const slot = electiveSlots[i];
        const grade = this.gradeFor(placement.sectionSubject);
        const terms = placement.terms;

        // A known single term averages over that term alone; an elective whose
        // term nobody recorded falls back to the whole-year rule.
        const finalGrade = terms.length
          ? semesterAverage(grade, periods, terms) || 0
          : subjectFinalGrade(grade, periods);

        if (finalGrade) finalGrades.push(finalGrade);

        return this.renderRow({
          key: `elective-${i}`,
          label: placement.label || slot.text,
          units: slot.units,
          periodValues: periods.map((p) => (grade ? Number(grade[p.dbField]) || 0 : 0)),
          // Grey the terms this elective is not taken in. With no subject
          // placed, the row greys to the form's own pattern; with one placed
          // whose term is unrecorded, nothing is greyed, because greying a
          // term that actually holds a mark would hide it.
          greyTerms: placement.greyTerms,
          finalGrade,
        });
      }
    );

    const totalUnits = coreSlots
      .concat(electiveSlots)
      .reduce((sum, slot) => sum + (Number(slot.units) || 0), 0);

    const classConduct =
      sectionStudent.classConducts && sectionStudent.classConducts.length !== 0
        ? sectionStudent.classConducts[0]
        : null;

    const conductFinal = conductFinalGrade(classConduct, periods);

    // The club is 5% of the general average here too; the row itself prints
    // below Conduct as it does on every other card.
    const clubs = clubRows(this.props.clubStudents, this.props.schoolYear);
    const clubFinal = subjectFinalGrade(
      clubs.length ? clubs[0].membership : null, periods);

    const average = generalAverage(finalGrades, conductFinal, expectedCount, clubFinal);
    const adviser = _.find(teachers, (t) => t.teacherId === section.teacherId);

    const columnCount = periods.length + 4;

    return (
      <CardShell {...this.props} adviser={adviser}>
        <Grid.Row>
          <Grid.Column width={16}>
            <Table celled size="small">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell
                    colSpan={String(columnCount)}
                    textAlign="center"
                  >
                    LEARNING PROGRESS AND ACHIEVEMENT
                  </Table.HeaderCell>
                </Table.Row>
                <Table.Row>
                  <Table.HeaderCell rowSpan="2" textAlign="center">
                    Learning Areas
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    colSpan={String(periods.length)}
                    textAlign="center"
                  >
                    TERM
                  </Table.HeaderCell>
                  <Table.HeaderCell rowSpan="2" textAlign="center">
                    Units
                  </Table.HeaderCell>
                  <Table.HeaderCell rowSpan="2" textAlign="center">
                    Final Grade
                  </Table.HeaderCell>
                  <Table.HeaderCell rowSpan="2" textAlign="center">
                    Remarks
                  </Table.HeaderCell>
                </Table.Row>
                <Table.Row>
                  {periods.map((p, i) => (
                    <Table.HeaderCell
                      key={p.index}
                      collapsing
                      textAlign="center"
                      style={
                        i === 0
                          ? { borderLeft: "1px solid rgba(34,36,38,.1)" }
                          : undefined
                      }
                    >
                      {p.shortLabel}
                    </Table.HeaderCell>
                  ))}
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {this.renderGroupHeader("Core Subjects", columnCount)}
                {coreRows}

                {this.renderGroupHeader("Elective Subjects", columnCount)}
                {electiveRows}

                <Table.Row className="students-table-row">
                  <Table.Cell
                    colSpan={String(periods.length + 1)}
                    textAlign="center"
                  >
                    <strong>
                      <em>General Average</em>
                    </strong>
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <strong>{totalUnits}</strong>
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <strong>
                      {/* The eye used to be the average's alternative, so
                          revealing it replaced the only control that could
                          hide it again - one way, until the page reloaded. It
                          stays beside the figure now. */}
                      {this.state.showAverage && average}
                      {this.canSeeAverage() && (
                        <Icon
                          name={this.state.showAverage ? "eye slash" : "eye"}
                          size="large"
                          title={
                            this.state.showAverage
                              ? "Hide the general average"
                              : "Show the general average"
                          }
                          style={{ cursor: "pointer", marginLeft: this.state.showAverage ? "0.5em" : 0 }}
                          onClick={this.toggleAverage}
                        />
                      )}
                    </strong>
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    {this.state.showAverage && average !== "INC" ? "Promoted" : ""}
                  </Table.Cell>
                </Table.Row>

                <Table.Row className="students-table-row">
                  <Table.Cell>
                    <span>Conduct</span>
                  </Table.Cell>
                  {periods.map((p) => {
                    const v = classConduct ? classConduct[p.conductField] : "";
                    return (
                      <Table.Cell key={p.index} textAlign="center">
                        {!v ? "" : v}
                      </Table.Cell>
                    );
                  })}
                  <Table.Cell />
                  <Table.Cell textAlign="center">{conductFinal}</Table.Cell>
                  <Table.Cell />
                </Table.Row>

                {/* Clubs print under Conduct and stay out of the average. */}
                {clubRows(this.props.clubStudents, this.props.schoolYear).map((club, i) => {
                  const clubFinal = subjectFinalGrade(club.membership, periods);
                  return (
                    <Table.Row key={`club-${i}`} className="students-table-row">
                      <Table.Cell>
                        <span>{club.label}</span>
                      </Table.Cell>
                      {periods.map((p) => (
                        <Table.Cell key={p.index} textAlign="center">
                          {clubMark(club.membership, scheme, p)}
                        </Table.Cell>
                      ))}
                      <Table.Cell />
                      <Table.Cell textAlign="center">
                        {!clubFinal ? "" : clubFinal}
                      </Table.Cell>
                      <Table.Cell />
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          </Grid.Column>
        </Grid.Row>
      </CardShell>
    );
  }

  /**
   * Decides which subject prints on each of the form's three elective rows.
   *
   * SectionSubject.sem is the registrar's term marker - "1", "2" or "3" - so
   * elective row N takes the subject marked for term N. Nothing else in the
   * Strengthened Grade 11 path reads sem, so a core subject left at "1,2,3" or
   * at blank is harmless.
   *
   * A subject with no usable sem still has to appear somewhere: it drops into
   * whichever rows are left, in order, rather than vanishing off the card. Its
   * term is unknown, so its row is not greyed.
   */
  placeElectives(coreSlots, electiveSlots) {
    const { sectionSubjects, scheme } = this.props;
    const periodIndexes = scheme.periods.map((p) => p.index);

    // Every category the Core block claims, parent rows and sub-rows alike.
    const coreValues = coreSlots.reduce(
      (values, slot) =>
        values.concat(slot.value, (slot.components || []).map((c) => c.value)),
      []
    );

    const electives = (sectionSubjects || []).filter(
      (o) => o.subject && !categoryInList(coreValues, o.subject.subjectCategory)
    );

    const termsOf = (sectionSubject) =>
      String(sectionSubject.sem || "")
        .split(/[^0-9]+/)
        .filter(Boolean)
        .map(Number)
        .filter((t) => periodIndexes.indexOf(t) !== -1);

    const taken = [];
    const placements = electiveSlots.map((slot) => {
      // A subject marked "1,2,3" would otherwise match all three rows and print
      // three times, so a subject is only ever placed once.
      const match = _.find(
        electives,
        (o) => taken.indexOf(o) === -1 && termsOf(o).indexOf(slot.term) !== -1
      );
      if (match) taken.push(match);
      return match || null;
    });

    const unplaced = electives.filter((o) => taken.indexOf(o) === -1);

    return placements.map((sectionSubject, i) => {
      const slot = electiveSlots[i];

      if (!sectionSubject && unplaced.length) sectionSubject = unplaced.shift();

      const terms = sectionSubject ? termsOf(sectionSubject) : [slot.term];
      const greyTerms = terms.length
        ? periodIndexes.filter((index) => terms.indexOf(index) === -1)
        : [];

      return {
        sectionSubject: sectionSubject,
        label: sectionSubject ? sectionSubject.subject.name : null,
        // An empty terms list means "placed, but nobody recorded the term".
        terms: sectionSubject ? terms : [],
        greyTerms: greyTerms,
      };
    });
  }

  renderGroupHeader(label, columnCount) {
    return (
      <Table.Row className="students-table-row">
        <Table.Cell colSpan={String(columnCount)} style={GROUP_HEADER}>
          <strong>{label}</strong>
        </Table.Cell>
      </Table.Row>
    );
  }

  renderRow({ key, label, subRow, units, periodValues, greyTerms, finalGrade }) {
    const { scheme } = this.props;
    const grey = greyTerms || [];

    return (
      <Table.Row key={key} className="students-table-row">
        <Table.Cell>
          <span style={subRow ? SUB_ROW : undefined}>{label}</span>
        </Table.Cell>
        {scheme.periods.map((p, i) => {
          // A greyed term is one this subject is not taken in, so it prints
          // empty whatever is stored against it. An elective marked for term 1
          // came back from the registrar showing the same number in term 2 as
          // well - entered by a teacher who changed the term and saved before
          // the form had reloaded - and the card printed it inside the grey
          // box, which is the one place a reader is being told no mark exists.
          const notTaken = grey.indexOf(p.index) !== -1;

          return (
            <Table.Cell
              key={p.index}
              textAlign="center"
              style={notTaken ? NOT_TAKEN : undefined}
            >
              {notTaken || !periodValues[i] ? "" : periodValues[i]}
            </Table.Cell>
          );
        })}
        <Table.Cell textAlign="center">{units || ""}</Table.Cell>
        <Table.Cell textAlign="center">{!finalGrade ? "" : finalGrade}</Table.Cell>
        <Table.Cell textAlign="center">
          {subRow ? "" : passFailLabel(finalGrade, scheme.passingGrade)}
        </Table.Cell>
      </Table.Row>
    );
  }
}

export default CardSHS11;
